import { useFormContext } from "react-hook-form";
import { usePermission } from "../../../../../../../../components/PermissionGuard";
import { useState, useCallback, useMemo } from "react";
import { Box, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Typography } from "@mui/material";
import { formatarValorMonetario } from "../../../../../../../../helpers/masks";
import { useQuery } from "react-query";
import axiosClient from "../../../../../../../../lib/axios";
import { IDataListaTitulosReceber } from "../../../../../../../../types/api/financeiro/pesquisar-titulos-receber";
import moment from "moment";
import LoadingApiQuery from "../../../../../../../../components/LoadingApiQuery";

export default function AplicarCupomDesconto() {
  const { setValue, watch } = useFormContext();
  const [cupomDesconto, setCupomDesconto] = useState<TCupomDesconto | null>(null);

  const actualStep = watch('stepper');
  const titulosSelecionados = watch('selecionar_titulos');
  const valueRadio = watch('finalizacao.aplicarDiferencaComCupom');
  const permissaoDescontoCupom = usePermission('financeiro.contas-receber-permitir-desconto-cupom');

  const titulosKey = useMemo(() =>
    titulosSelecionados
      ?.map((titulo: IDataListaTitulosReceber) => titulo.id)
      .sort()
      .join('-') || ''
    , [titulosSelecionados]);

  const limparCupom = useCallback(() => {
    setCupomDesconto(null);
    setValue('finalizacao.aplicarDiferencaComCupom', false);
    setValue('finalizacao.cupomDesconto', '');
  }, [setValue]);

  const handleSuccess = useCallback((data: any) => {
    if (data.data.message?.startsWith("Erro ao validar cupom")) {
      limparCupom();
      return;
    }
    const novoCupom = data.data;
    setCupomDesconto(novoCupom);
    if (novoCupom?.valor) {
      setValue('finalizacao.cupomDesconto', novoCupom);
      // Se já estava marcado para aplicar, mantém o valor
      if (valueRadio) {
        setValue('finalizacao.aplicarDiferencaComCupom', true);
      }
    }
  }, [limparCupom, setValue, valueRadio]);

  const { isLoading } = useQuery({
    queryKey: ['validar-cupom', titulosKey, actualStep],
    queryFn: () =>
      axiosClient.get("/financeiro/cupons/validar-cupom", {
        params: {
          titulos: titulosSelecionados?.map((titulo: IDataListaTitulosReceber) => titulo.id) || [],
        },
      }),
    onSuccess: handleSuccess,
    onError: limparCupom,
    retry: false,
    enabled: actualStep === 2 && titulosSelecionados?.length > 0,
    staleTime: 0,
    cacheTime: 5 * 60 * 1000,
    refetchOnMount: true,
  });


  const handleChangeRadio = (value: boolean) => {
    setValue('finalizacao.aplicarDiferencaComCupom', value);
    setValue('finalizacao.aplicarDiferencaSemCupom', false);
    setValue('finalizacao.cupomDesconto', value && cupomDesconto ? cupomDesconto : '');
  }

  const podeExibirCupom = useMemo(() =>
    cupomDesconto &&
    cupomDesconto.valor != null &&
    cupomDesconto.valor > 0 &&
    (cupomDesconto.valor < 1 || (cupomDesconto.valor >= 1 && permissaoDescontoCupom))
    , [cupomDesconto, permissaoDescontoCupom]);

  if (actualStep !== 2 || !titulosSelecionados?.length) {
    return null;
  }

  if (!podeExibirCupom) {
    if (valueRadio) {
      setValue('finalizacao.aplicarDiferencaComCupom', false);
      setValue('finalizacao.cupomDesconto', '');
    }
    return null;
  }

  return (
    <>
      {isLoading && <LoadingApiQuery />}
      {cupomDesconto && !isLoading && (
        <Box
          p={2}
          borderRadius={1}
          bgcolor="#fff4e5"
          sx={{ color: "#663c00" }}
          mt={2}
        >
          <Typography variant="subtitle2">
            Existe um cupom de desconto disponível para títulos constantes nessa seleção.
          </Typography>
          <Typography variant="subtitle2">
            {`Código: ${cupomDesconto.codigo} - Valor: ${formatarValorMonetario(
              cupomDesconto.valor
            )} - Válido até: ${moment(cupomDesconto.data_validade).format("DD/MM/YYYY")}`}
          </Typography>

          <FormControl>
            <FormLabel id="aplicar-cupom-desconto">Aplicar cupom de desconto?</FormLabel>
            <RadioGroup
              row
              aria-labelledby="aplicar-cupom-desconto"
              value={valueRadio}
              onChange={(e) => handleChangeRadio(e.target.value as any)}
            >
              <FormControlLabel value={true} control={<Radio color="warning" size="small" />} label="Sim" />
              <FormControlLabel value={false} control={<Radio color="warning" size="small" />} label="Não" />
            </RadioGroup>
          </FormControl>
        </Box>
      )}
    </>
  );
}

export type TCupomDesconto = {
  id: number;
  codigo: string;
  valor: number;
  data_validade: string;
  criado_por: string;
  criado_em: string;
};